import type { RecordEquipmentLocationDto } from '@geartracker/dto';
import type {
  EquipmentLocationHistoryItem,
  EquipmentMapPoint,
} from '@geartracker/types';

import axios, { endpoints } from 'src/utils/axios';
import { equipmentKeys } from './equipment.api';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const equipmentLocationKeys = {
  all: ['equipment-locations'] as const,
  fleetMap: () => [...equipmentLocationKeys.all, 'fleet-map'] as const,
  histories: () => [...equipmentLocationKeys.all, 'history'] as const,
  historiesForEquipment: (equipmentId: number) =>
    [...equipmentLocationKeys.histories(), equipmentId] as const,
  history: (equipmentId: number, take?: number) =>
    [
      ...equipmentLocationKeys.historiesForEquipment(equipmentId),
      take,
    ] as const,
};

export const getEquipmentFleetLocations = async (): Promise<
  EquipmentMapPoint[]
> => {
  const response = await axios.get(endpoints.equipment.locations.fleetMap);
  return response.data;
};

export const getEquipmentLocationHistory = async (
  equipmentId: number,
  take?: number,
): Promise<EquipmentLocationHistoryItem[]> => {
  const response = await axios.get(
    endpoints.equipment.locations.root(equipmentId),
    { params: take ? { take } : undefined },
  );
  return response.data;
};

export const recordEquipmentLocation = async ({
  equipmentId,
  location,
}: {
  equipmentId: number;
  location: RecordEquipmentLocationDto;
}): Promise<EquipmentLocationHistoryItem> => {
  const response = await axios.post(
    endpoints.equipment.locations.root(equipmentId),
    location,
  );
  return response.data;
};

/*
  HOOKS
*/

/** Fleet-wide "last known position" list, for the map view. */
export const useEquipmentFleetLocations = (options?: {
  disabled?: boolean;
}) => {
  return useQuery({
    queryKey: equipmentLocationKeys.fleetMap(),
    queryFn: getEquipmentFleetLocations,
    enabled: !options?.disabled,
  });
};

/** Full position history of a single equipment, most recent first. */
export const useEquipmentLocationHistory = (
  equipmentId: number,
  options?: { take?: number; disabled?: boolean },
) => {
  return useQuery({
    queryKey: equipmentLocationKeys.history(equipmentId, options?.take),
    queryFn: () => getEquipmentLocationHistory(equipmentId, options?.take),
    enabled: !!equipmentId && !options?.disabled,
  });
};

/**
 * Records a passive GPS capture, decoupled from whichever action (inspection,
 * NFC scan, ...) triggered it — call this whenever the device's geolocation
 * resolves, without blocking that action's own request. Silent on both
 * success and failure: this is a best-effort background capture, not
 * something the user explicitly asked for.
 */
export const useRecordEquipmentLocation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: recordEquipmentLocation,
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: equipmentLocationKeys.historiesForEquipment(
          variables.equipmentId,
        ),
      });
      void queryClient.invalidateQueries({
        queryKey: equipmentLocationKeys.fleetMap(),
      });
      void queryClient.invalidateQueries({
        queryKey: equipmentKeys.detail(variables.equipmentId),
      });
    },
  });
};
